"use client";
import { Alert, Box, Center, Stack } from "@chakra-ui/react";
import { RichText } from "@graphcms/rich-text-react-renderer";
import { BlockquoteComponent } from "@areahub/ui/marketing/blockquote-component/src/blockquote-component";
import { FAQSection } from "@areahub/ui/marketing/faq/faq";
import { ProductReviewCard } from "@areahub/ui/marketing/product-review-card/src/product-review-card";
import { ProductReviewGrid } from "@areahub/ui/marketing/product-review-card/src/product-review-grid";
import { ReportCta } from "@areahub/ui/marketing/report-cta/report-cta";
import { ShareSocialMediaButtons } from "@areahub/ui/marketing/social-media/share-buttons";
import { richTextRenderers } from "components/individuals/mdx-components";
import { Post } from "lib/gcms";
import { getWebUrl } from "@areahub/config/src/urls";
import { usePathname } from "next/navigation";

export interface KCPostProps {
  post: Post;
}

export const KCPost = ({ post }: KCPostProps) => {
  const pathname = usePathname();
  const description = post?.excerpt;
  const category = post.aggregatePostPage;

  return (
    <>
      <Stack
        flexDirection="column"
        flex="auto"
        alignItems="flex-start"
        spacing={{
          base: "6",
        }}
      >
        <RichText
          content={post.kcContent?.json}
          references={post.kcContent?.references}
          renderers={{
            ...richTextRenderers,
            embed: {
              FaqSection: ({ header: title, faqs }) => (
                <FAQSection
                  title={title}
                  faqs={faqs}
                  renderAnswer={(answer) => <RichText content={answer?.raw} />}
                />
              ),
              ReportCta: ({ description, buttons }) => (
                <>
                  <ReportCta buttons={buttons} content={description?.text} />
                </>
              ),
              BlockquoteComponent: ({ description }) => (
                <>
                  <BlockquoteComponent>
                    <RichText
                      content={description?.raw}
                      renderers={richTextRenderers}
                    />
                  </BlockquoteComponent>
                </>
              ),
              ProductReviewCard: (props) => (
                <Center w="full">
                  <ProductReviewCard {...props} />
                </Center>
              ),
              ProductReviewGrid: ({ description, disclaimer, products }) => (
                <>
                  <RichText
                    content={description?.raw}
                    renderers={richTextRenderers}
                  />
                  <ProductReviewGrid products={products} />
                  <Alert
                    as="blockquote"
                    mt="4"
                    role="disclaimer"
                    status="info"
                    variant="left-accent"
                    rounded="4px"
                    my="1.5rem"
                    fontSize="xs"
                    fontStyle="italic"
                    color="subtle"
                  >
                    <RichText content={disclaimer?.raw} />
                  </Alert>
                </>
              ),
            },
          }}
        />

        <ShareSocialMediaButtons
          shareUrl={`${getWebUrl()}${pathname}`}
          shareHashtag="AreaHub"
          shareTitle={post?.title}
          summary={description || ""}
          display={{ base: "flex", md: "none" }}
          hashtags={[...(category?.title ? [category.title] : [])]?.map(
            (tag: string) => tag.replace(" ", ""),
          )}
        />
      </Stack>
      <Box
        width="10rem"
        justifySelf="left"
        display={{ base: "none", lg: "block" }}
      />
    </>
  );
};

export default KCPost;
